import React, { useMemo, useState, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
import { ArrowLeftIcon, ArrowRightIcon, BookOpenIcon, CheckIcon, ChevronRightIcon, CrownIcon, GemIcon, LandmarkIcon, LockKeyholeIcon, MessageCircleIcon, ShieldIcon, SparklesIcon, SwordsIcon, UsersIcon } from 'lucide-react';
type Screen = 'landing' | 'misl' | 'google' | 'whatsapp' | 'share' | 'join' | 'dashboard' | 'packet' | 'reveal' | 'contribution' | 'awakening' | 'gates' | 'challenge' | 'leaderboard' | 'result' | 'seats' | 'attendance' | 'victory' | 'admin';
type TreasuryCard = {
  title: string;
  era: string;
  type: string;
  note: string;
  image: string;
};
const portalFrames = ['https://cdn.magicpatterns.com/uploads/ux69z3BxhE3RknrKnbGDMW/portal_1.png', 'https://cdn.magicpatterns.com/uploads/8dy9JCdDxozgGGW6n7ygjX/portal2.png', 'https://cdn.magicpatterns.com/uploads/qmoeHyZWK94v55FDQGeZin/portal3.png', 'https://cdn.magicpatterns.com/uploads/uwBEcuhEQptJbLVZ3ofyA7/portal4.png', 'https://cdn.magicpatterns.com/uploads/axcJqkLa5HT8C6A6MLycui/portal5.png'];
const treasuryCards: TreasuryCard[] = [{
  title: 'Maharaja Ranjit Singh',
  era: 'Lahore Court Collection · Founders’ Edition',
  type: 'Founding Face',
  note: 'A sovereign presence at the centre of the Lahore Court.',
  image: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png'
}, {
  title: 'Maharani Jind Kaur',
  era: 'Lahore Court Collection · Founders’ Edition',
  type: 'Regent of Punjab',
  note: 'A living record of leadership, memory, and defiance.',
  image: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png'
}, {
  title: 'Hari Singh Nalwa',
  era: 'Lahore Court Collection · Founders’ Edition',
  type: 'Commander-in-Chief',
  note: 'A standard of courage carried to the frontier of the kingdom.',
  image: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png'
}, {
  title: 'The Lahore Darbar',
  era: 'Court Scenes · Treasury Rare',
  type: 'Living Tableau',
  note: 'The court becomes real again when its stories return to the people.',
  image: 'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png'
}, {
  title: 'Golden Throne of Lahore',
  era: 'Court Treasures · Founders’ Edition',
  type: 'Seat of the Sarkar',
  note: 'A symbol of responsibility: the court opens through collective care.',
  image: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png'
}];
const mislOptions = [{
  name: 'Sukerchakia',
  detail: 'Builders of the court',
  icon: LandmarkIcon
}, {
  name: 'Ahluwalia',
  detail: 'Keepers of the standard',
  icon: ShieldIcon
}, {
  name: 'Bhangi',
  detail: 'Makers and defenders',
  icon: SwordsIcon
}];
export function CourtRushPage() {
  const screenInit = useScreenInit();
  const initialScreen = screenInit.screen === 'start' ? 'misl' : screenInit.screen === 'contribute' ? 'contribution' : screenInit.screen === 'awakened' ? 'awakening' : screenInit.screen as Screen || 'landing';
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [misl, setMisl] = useState('Sukerchakia');
  const [selectedCard, setSelectedCard] = useState<TreasuryCard>(treasuryCards[0]);
  const [packetOpened, setPacketOpened] = useState(false);
  const content = useMemo(() => getScreenContent(screen), [screen]);
  function move(next: Screen) {
    setScreen(next);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function openPacket() {
    setPacketOpened(true);
    window.setTimeout(() => move('reveal'), 850);
  }
  return <main className="min-h-screen w-full overflow-hidden bg-[#100d0a] text-[#f6efdf]" data-id="element-4080" data-mp="lk2wrt6at00e">
      <PortalBackdrop phase={screen} data-id="element-4081" data-mp="2bzc6j3l8m2pm" />
      <header className="relative z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8" data-id="element-4082" data-mp="2c2htac6e8si6">
        <button type="button" onClick={() => move('landing')} className="flex items-center gap-2 rounded-full text-left font-serif text-base tracking-wide text-[#f8e5b2] transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e4bd63]" aria-label="Return to Court Rush home" data-id="element-4083" data-mp="2l136drt3jfbk">
          <CrownIcon className="h-5 w-5" aria-hidden="true" data-id="element-4084" data-mp="1ocds0bgoh4uc" />
          COURT RUSH
        </button>
        <div className="hidden items-center gap-2 text-xs uppercase tracking-[0.19em] text-[#cdb987] sm:flex" data-id="element-4085" data-mp="223s9ueyumzzt">
          <span className="h-1.5 w-1.5 rounded-full bg-[#d9a744]" data-id="element-4086" data-mp="1xa6476r3rwdi" />
          Lahore Treasury
        </div>
      </header>

      <AnimatePresence mode="wait" data-id="element-4087" data-mp="14mtl56k5i6oy">
        <motion.section key={screen} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="relative z-10 mx-auto flex min-h-[calc(100vh-76px)] w-full max-w-6xl items-center px-5 pb-12 sm:px-8" data-id="element-4088" data-mp="14ls8ixnhlc54">
          {screen === 'landing' && <Landing onStart={() => move('misl')} data-id="element-4089" data-mp="3ta1pgsban9z" />}
          {screen === 'misl' && <MislSetup active={misl} onChoose={setMisl} onBack={() => move('landing')} onNext={() => move('google')} data-id="element-4090" data-mp="7t2d0193b7j1" />}
          {screen === 'packet' && <PacketOpening opened={packetOpened} onOpen={openPacket} onBack={() => move('dashboard')} data-id="element-4091" data-mp="3v0wa1mpz7dx6" />}
          {screen === 'reveal' && <CardReveal card={selectedCard} onContinue={() => move('contribution')} onSelect={setSelectedCard} data-id="element-4092" data-mp="1zhs6r6oms8gn" />}
          {screen === 'awakening' && <Awakening onContinue={() => move('gates')} data-id="element-4093" data-mp="1qoyca20m52i7" />}
          {screen === 'gates' && <CourtGates onEnter={() => move('challenge')} data-id="element-4094" data-mp="1lqws1drgax55" />}
          {screen === 'dashboard' && <Dashboard misl={misl} onOpen={() => move('packet')} onAwaken={() => move('awakening')} data-id="element-4095" data-mp="1gsd6hegrrndb" />}
          {!['landing', 'misl', 'packet', 'reveal', 'awakening', 'gates', 'dashboard'].includes(screen) && <StandardStep screen={screen} content={content} onBack={() => move(previousScreen(screen))} onNext={() => move(nextScreen(screen))} data-id="element-4096" data-mp="324dbzdk7i8h0" />}
        </motion.section>
      </AnimatePresence>
    </main>;
}
type PortalBackdropProps = {
  phase: Screen;
};
function PortalBackdrop({
  phase
}: PortalBackdropProps) {
  const portalIndex = phase === 'dashboard' ? 1 : phase === 'awakening' ? 2 : phase === 'gates' ? 3 : phase === 'victory' ? 4 : 0;
  const shouldShowPortal = ['dashboard', 'awakening', 'gates', 'victory'].includes(phase);
  return <div className="pointer-events-none absolute inset-0 overflow-hidden bg-[#100d0a]" aria-hidden="true" data-id="element-4097" data-mp="2qqcy1ek9o9jj">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(153,104,25,0.19),_transparent_58%)]" data-id="element-4098" data-mp="2vmtnqd746vyq" />
      {shouldShowPortal && <AnimatePresence mode="wait" data-id="element-4099" data-mp="ayfkuna0fo88">
          <motion.img key={`${phase}-${portalIndex}`} src={portalFrames[portalIndex]} alt="" initial={{
        opacity: 0,
        scale: 1.04
      }} animate={{
        opacity: phase === 'gates' ? 0.72 : 0.5,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.02
      }} transition={{
        duration: 1.1,
        ease: 'easeInOut'
      }} className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-4100" data-mp="axdznpza5lcs" />
        </AnimatePresence>}
      <div className="absolute inset-0 bg-[#100d0a]/55" data-id="element-4101" data-mp="1esy3x61uqf6f" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#100d0a] to-transparent" data-id="element-4102" data-mp="1esfz3l14ismw" />
    </div>;
}
function Landing({
  onStart
}: {
  onStart: () => void;
}) {
  return <div className="mx-auto max-w-3xl py-10 text-center" data-id="element-4103" data-mp="1oreyse3wvdxr">
      <p className="mb-5 text-xs font-semibold uppercase tracking-[0.27em] text-[#d9ad55]" data-id="element-4104" data-mp="3vgtjnf80d512">
        A living court of the Lahore Treasury
      </p>
      <h1 className="font-serif text-5xl leading-[0.94] text-[#fbf2dd] sm:text-7xl" data-id="element-4105" data-mp="3qk354wbuqhhr">
        Enter the Court.
      </h1>
      <p className="mx-auto mt-7 max-w-xl text-base leading-7 text-[#d5c8b1] sm:text-lg" data-id="element-4106" data-mp="262k7tmu5jat5">
        Gather your Misl, unlock a Treasury packet, and help awaken the court
        before the gates open.
      </p>
      <button type="button" onClick={onStart} className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#d7a846] px-6 py-3.5 text-sm font-bold text-[#20160c] transition hover:bg-[#f0ca75] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-4107" data-mp="219ho06f6ffq5">
        Begin the rush <ArrowRightIcon className="h-4 w-4" data-id="element-4108" data-mp="1dbsdyw0k6y8z" />
      </button>
      <div className="mt-14 grid gap-3 text-left sm:grid-cols-3" data-id="element-4109" data-mp="1ddda2q2iup8y">
        {['Choose your Misl', 'Claim a Treasury card', 'Open the Court'].map((item, index) => <div key={item} className="border border-[#caa452]/25 bg-[#19140f]/85 p-4" data-id="element-4110" data-mp="37qv1io61s699" data-mpi={index}>
              <span className="font-serif text-2xl text-[#d8ae5a]" data-id="element-4111" data-mp="3cmk2mvxptv7o">
                0{index + 1}
              </span>
              <p className="mt-4 text-sm text-[#eee2c9]" data-id="element-4112" data-mp="2zazydhn94r1v">{item}</p>
            </div>)}
      </div>
    </div>;
}
type MislSetupProps = {
  active: string;
  onChoose: (name: string) => void;
  onBack: () => void;
  onNext: () => void;
};
function MislSetup({
  active,
  onChoose,
  onBack,
  onNext
}: MislSetupProps) {
  return <div className="mx-auto w-full max-w-4xl py-8" data-id="element-4113" data-mp="3e80pbixmchza">
      <button type="button" onClick={onBack} className="mb-8 inline-flex items-center gap-2 text-sm text-[#d8c59c] hover:text-white" data-id="element-4114" data-mp="3e6fya3aijh66">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-4115" data-mp="349l0jmm2dksy" /> Back
      </button>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d9ad55]" data-id="element-4116" data-mp="398o575y4tp0e">
        First, choose your side
      </p>
      <h1 className="mt-3 font-serif text-4xl text-[#fbf2dd] sm:text-5xl" data-id="element-4117" data-mp="1su65wphbv510">
        Which Misl will you gather?
      </h1>
      <div className="mt-9 grid gap-4 md:grid-cols-3" data-id="element-4118" data-mp="1xvbvwfjlse6u">
        {mislOptions.map(option => {
        const Icon = option.icon;
        const selected = active === option.name;
        return <button key={option.name} type="button" onClick={() => onChoose(option.name)} className={`min-h-48 border p-6 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e4bd63] ${selected ? 'border-[#e0b653] bg-[#2c2114]' : 'border-[#a68a55]/30 bg-[#18130e]/85 hover:border-[#cba650]'}`} data-id="element-4119" data-mp="1ldj2irg2dds2">
              <Icon className="h-7 w-7 text-[#d9ad55]" data-id="element-4120" data-mp="12mrvhwjjtzgu" />
              <h2 className="mt-10 font-serif text-2xl text-[#f7ecd5]" data-id="element-4121" data-mp="1gcljydr56hq1">
                {option.name}
              </h2>
              <p className="mt-2 text-sm text-[#c9bfae]" data-id="element-4122" data-mp="1bktu65ft8g0y">{option.detail}</p>
              {selected && <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#e5bc66]" data-id="element-4123" data-mp="nugb34f7drii">
                  <CheckIcon className="h-3.5 w-3.5" data-id="element-4124" data-mp="iv3lwd0v08cr" /> Selected
                </span>}
            </button>;
      })}
      </div>
      <button type="button" onClick={onNext} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#d7a846] px-6 py-3 text-sm font-bold text-[#21170d]" data-id="element-4125" data-mp="2nx8u1j9t983c">
        Continue <ArrowRightIcon className="h-4 w-4" data-id="element-4126" data-mp="2xmscintm2zxo" />
      </button>
    </div>;
}
function Dashboard({
  misl,
  onOpen,
  onAwaken
}: {
  misl: string;
  onOpen: () => void;
  onAwaken: () => void;
}) {
  return <div className="mx-auto w-full max-w-5xl py-8" data-id="element-4127" data-mp="24zy4wrhw7v1m">
      <div className="flex flex-col justify-between gap-5 border-b border-[#c9a554]/25 pb-6 sm:flex-row sm:items-end" data-id="element-4128" data-mp="29yr5xkfzttkw">
        <div data-id="element-4129" data-mp="2e0cq7quuxa54">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d7a847]" data-id="element-4130" data-mp="2e0vzykacss82">
            {misl} Misl
          </p>
          <h1 className="mt-2 font-serif text-4xl text-[#fbf2dd]" data-id="element-4131" data-mp="3ld4pwe7mlovn">
            Your Court Room
          </h1>
        </div>
        <div className="text-sm text-[#d6c6aa]" data-id="element-4132" data-mp="3ph460vozuloh">
          <strong className="font-serif text-2xl text-[#efc770]" data-id="element-4133" data-mp="1dquwbz4ceeyb">8</strong>{' '}
          gathered · 4 more to awaken
        </div>
      </div>
      <div className="mt-7 grid gap-5 lg:grid-cols-[1.3fr_0.7fr]" data-id="element-4134" data-mp="19sfdm7fansqp">
        <section className="border border-[#c9a554]/30 bg-[#19140f]/90 p-6" data-id="element-4135" data-mp="14yb53jqdrcni">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d8aa50]" data-id="element-4136" data-mp="101b0ld96ihyi">
            Today’s Treasury packet
          </p>
          <div className="mt-7 flex flex-col gap-7 sm:flex-row sm:items-center" data-id="element-4137" data-mp="v2q0ddtxfya2">
            <TreasuryCardFace card={treasuryCards[1]} compact data-id="element-4138" data-mp="q1k6hcoj64yy" />
            <div data-id="element-4139" data-mp="m08ampf2cnib">
              <h2 className="font-serif text-3xl text-[#f7ecd5]" data-id="element-4140" data-mp="m1rt8mo6zan9">
                The packet is waiting.
              </h2>
              <p className="mt-3 max-w-sm text-sm leading-6 text-[#cbbfa8]" data-id="element-4141" data-mp="h3864ixv3stb">
                Every draw returns an object from the Treasury to the living
                court.
              </p>
              <button type="button" onClick={onOpen} className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#d7a846] px-5 py-2.5 text-sm font-semibold text-[#f4d482] hover:bg-[#d7a846] hover:text-[#21170d]" data-id="element-4142" data-mp="2bg7rytsu5j91">
                Open packet <ChevronRightIcon className="h-4 w-4" data-id="element-4143" data-mp="z3kqtt8sgqhm" />
              </button>
            </div>
          </div>
        </section>
        <section className="border border-[#c9a554]/30 bg-[#19140f]/90 p-6" data-id="element-4144" data-mp="1450abgvbioxw">
          <SparklesIcon className="h-6 w-6 text-[#d9ad55]" data-id="element-4145" data-mp="pcyfib83um4q" />
          <h2 className="mt-9 font-serif text-2xl text-[#f7ecd5]" data-id="element-4146" data-mp="u59bx43mzpsj">
            Court Awakening
          </h2>
          <p className="mt-3 text-sm leading-6 text-[#cbbfa8]" data-id="element-4147" data-mp="gemhvp8tmo2s">
            Your Misl has gathered enough voices to light the threshold.
          </p>
          <button type="button" onClick={onAwaken} className="mt-6 text-sm font-semibold text-[#efc770] underline decoration-[#d7a846]/60 underline-offset-4" data-id="element-4148" data-mp="6ff26vem6hci">
            Witness the opening
          </button>
        </section>
      </div>
    </div>;
}
function PacketOpening({
  opened,
  onOpen,
  onBack
}: {
  opened: boolean;
  onOpen: () => void;
  onBack: () => void;
}) {
  return <div className="mx-auto flex w-full max-w-xl flex-col items-center py-10 text-center" data-id="element-4149" data-mp="1r5uyzv4ljh7">
      <button type="button" onClick={onBack} className="self-start text-sm text-[#d8c59c] hover:text-white" data-id="element-4150" data-mp="3swxkpy4zqu3h">
        <ArrowLeftIcon className="mr-2 inline h-4 w-4" data-id="element-4151" data-mp="aprccrov2sge" /> Court room
      </button>
      <p className="mt-10 text-xs uppercase tracking-[0.25em] text-[#d8aa50]" data-id="element-4152" data-mp="5rpuga368z6i">
        Treasury packet no. 07
      </p>
      <motion.button type="button" onClick={onOpen} disabled={opened} animate={opened ? {
      rotateY: 90,
      scale: 1.08,
      opacity: 0
    } : {
      y: [0, -8, 0]
    }} transition={{
      duration: opened ? 0.7 : 2.5,
      repeat: opened ? 0 : Infinity
    }} className="mt-10 cursor-pointer disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e4bd63]" data-id="element-4153" data-mp="kglw9pemxfyo">
        <TreasuryCardFace card={treasuryCards[0]} data-id="element-4154" data-mp="16h2rpry35a4v" />
      </motion.button>
      <h1 className="mt-10 font-serif text-4xl text-[#fbf2dd]" data-id="element-4155" data-mp="1b92tlpxpud43">
        Press the seal to reveal.
      </h1>
      <p className="mt-3 text-sm text-[#cbbfa8]" data-id="element-4156" data-mp="1faxzl7qldeo7">
        A real Treasury card, returned to your Misl.
      </p>
    </div>;
}
function CardReveal({
  card,
  onContinue,
  onSelect
}: {
  card: TreasuryCard;
  onContinue: () => void;
  onSelect: (card: TreasuryCard) => void;
}) {
  return <div className="mx-auto w-full max-w-5xl py-8" data-id="element-4157" data-mp="1u7oyzh09mqdt">
      <div className="grid items-center gap-10 md:grid-cols-[0.8fr_1.2fr]" data-id="element-4158" data-mp="1u5chf4yf6i1a">
        <TreasuryCardFace card={card} data-id="element-4159" data-mp="3r19yc98o1dn7" />
        <div data-id="element-4160" data-mp="3vysbycxmoh1j">
          <p className="text-xs uppercase tracking-[0.23em] text-[#d8aa50]" data-id="element-4161" data-mp="3vya56mdcakfd">
            Added to your Misl
          </p>
          <h1 className="mt-3 font-serif text-5xl text-[#fbf2dd]" data-id="element-4162" data-mp="2d4pf1f9oqk4k">
            {card.title}
          </h1>
          <p className="mt-5 max-w-md text-lg leading-8 text-[#d7c8ae]" data-id="element-4163" data-mp="28c5q5m5js2kg">
            {card.note}
          </p>
          <button type="button" onClick={onContinue} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#d7a846] px-6 py-3 text-sm font-bold text-[#21170d]" data-id="element-4164" data-mp="24bvkkalo3e64">
            Make a contribution <ArrowRightIcon className="h-4 w-4" data-id="element-4165" data-mp="2s0ycqcdxgpev" />
          </button>
        </div>
      </div>
      <div className="mt-12 border-t border-[#c9a554]/25 pt-6" data-id="element-4166" data-mp="2rz3sz1ee73cd">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#bea16a]" data-id="element-4167" data-mp="2n0aasvo5uo5v">
          Treasury collection
        </p>
        <div className="flex gap-3 overflow-x-auto pb-2" data-id="element-4168" data-mp="2i4uyi85sx0bk">
          {treasuryCards.map(item => <button type="button" aria-label={`View ${item.title}`} onClick={() => onSelect(item)} key={item.title} className={`shrink-0 ${item.title === card.title ? 'ring-2 ring-[#e4bd63]' : ''}`} data-id="element-4169" data-mp="1bklom6iqdt64">
              <TreasuryCardFace card={item} compact data-id="element-4170" data-mp="3bq91r4m5m8xk" />
            </button>)}
        </div>
      </div>
    </div>;
}
function TreasuryCardFace({
  card,
  compact = false
}: {
  card: TreasuryCard;
  compact?: boolean;
}) {
  return <article className={`relative overflow-hidden border border-[#e1bb68]/70 bg-[#241b12] text-left shadow-[0_18px_45px_rgba(0,0,0,0.38)] ${compact ? 'h-44 w-28' : 'h-[25rem] w-64 sm:w-72'}`} data-id="element-4171" data-mp="3pgmb9bep1vp0">
      <img src={card.image} alt={card.title} className="h-full w-full object-cover" data-id="element-4172" data-mp="3fr2qu1bc1ts1" />
      <div className="absolute inset-x-0 bottom-0 bg-[#120e09]/85 p-2.5" data-id="element-4173" data-mp="3koukazrmlnyc">
        <p className="text-[7px] uppercase tracking-[0.15em] text-[#e2ba67]" data-id="element-4174" data-mp="ca2n1cogs5a3">
          {card.type}
        </p>
        <h2 className={`${compact ? 'text-xs' : 'text-xl'} mt-1 font-serif leading-tight text-[#fff0ce]`} data-id="element-4175" data-mp="h12mopoyx1ga">
          {card.title}
        </h2>
      </div>
      <div className="absolute inset-1 border border-[#e4bd63]/45 pointer-events-none" data-id="element-4176" data-mp="1wrv25mh22ctb" />
    </article>;
}
function Awakening({
  onContinue
}: {
  onContinue: () => void;
}) {
  return <div className="mx-auto max-w-2xl py-12 text-center" data-id="element-4177" data-mp="2biu4wgv7m74z">
      <motion.div initial={{
      opacity: 0,
      scale: 0.86
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 1.3
    }} data-id="element-4178" data-mp="26ippku61figc">
        <SparklesIcon className="mx-auto h-10 w-10 text-[#edc76f]" data-id="element-4179" data-mp="1dutxk2reyepw" />
      </motion.div>
      <p className="mt-9 text-xs uppercase tracking-[0.27em] text-[#edc76f]" data-id="element-4180" data-mp="1rubnilgg3y0u">
        Court Awakening
      </p>
      <h1 className="mt-4 font-serif text-5xl leading-tight text-[#fff1d2] sm:text-6xl" data-id="element-4181" data-mp="1nt9n41aslfxa">
        The seal begins to burn.
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-[#e2d2b4]" data-id="element-4182" data-mp="2z6ltojxxscn5">
        Each card returned to the court has brought the threshold closer. The
        gates are ready to answer.
      </p>
      <button type="button" onClick={onContinue} className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#e2b656] px-6 py-3.5 text-sm font-bold text-[#21170d]" data-id="element-4183" data-mp="2jy4ug3bld83m">
        Approach the gates <ArrowRightIcon className="h-4 w-4" data-id="element-4184" data-mp="2sy9yq4lhnqdk" />
      </button>
    </div>;
}
function CourtGates({
  onEnter
}: {
  onEnter: () => void;
}) {
  return <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-end py-10 text-center" data-id="element-4185" data-mp="36w8089epnsbn">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1.15,
      delay: 0.2
    }} className="rounded-full border border-[#f0cc72]/50 bg-[#1c1309]/70 px-4 py-2 text-xs uppercase tracking-[0.21em] text-[#f0cb72]" data-id="element-4186" data-mp="32uwisr5zqdej">
        <LockKeyholeIcon className="mr-2 inline h-3.5 w-3.5" data-id="element-4187" data-mp="3lp1q8orlzquk" /> The Court is
        open
      </motion.div>
      <h1 className="mt-6 font-serif text-5xl text-[#fff1d2] sm:text-6xl" data-id="element-4188" data-mp="91i8aglnqptf">
        Enter together.
      </h1>
      <p className="mt-4 text-[#e1cfac]" data-id="element-4189" data-mp="d2ve4qor2t0c">
        The chamber beyond is lit by every Misl that answered the call.
      </p>
      <button type="button" onClick={onEnter} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#e2b656] px-6 py-3.5 text-sm font-bold text-[#21170d]" data-id="element-4190" data-mp="4409vk59nszo">
        Cross the threshold <ArrowRightIcon className="h-4 w-4" data-id="element-4191" data-mp="3qcrrm1yq1qs7" />
      </button>
    </div>;
}
function StandardStep({
  screen,
  content,
  onBack,
  onNext
}: {
  screen: Screen;
  content: {
    eyebrow: string;
    title: string;
    body: string;
    button: string;
    icon: typeof UsersIcon;
  };
  onBack: () => void;
  onNext: () => void;
}) {
  const Icon = content.icon;
  return <div className="mx-auto max-w-2xl py-12 text-center" data-id="element-4192" data-mp="37l8n1qp1u4o2">
      <Icon className="mx-auto h-9 w-9 text-[#dfb45c]" data-id="element-4193" data-mp="37kqhu4kesg40" />
      <p className="mt-7 text-xs uppercase tracking-[0.23em] text-[#d9ad55]" data-id="element-4194" data-mp="2oh5aupxp67y2">
        {content.eyebrow}
      </p>
      <h1 className="mt-4 font-serif text-5xl text-[#fbf2dd]" data-id="element-4195" data-mp="2jovmhec8dfxw">
        {content.title}
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-[#d6c7ad]" data-id="element-4196" data-mp="2ervgf3fdsd6s">
        {content.body}
      </p>
      <div className="mt-10 flex justify-center gap-3" data-id="element-4197" data-mp="29t20z5gjac3z">
        <button type="button" onClick={onBack} className="rounded-full border border-[#c9a554]/45 px-5 py-3 text-sm text-[#ead9b8]" data-id="element-4198" data-mp="37f7rohg3cyks">
          Back
        </button>
        <button type="button" onClick={onNext} className="rounded-full bg-[#d7a846] px-5 py-3 text-sm font-bold text-[#21170d]" data-id="element-4199" data-mp="2xh1sw8pl08k5">
          {content.button}
        </button>
      </div>
    </div>;
}
function getScreenContent(screen: Screen) {
  const steps: Partial<Record<Screen, {
    eyebrow: string;
    title: string;
    body: string;
    button: string;
    icon: typeof UsersIcon;
  }>> = {
    google: {
      eyebrow: 'Save your place',
      title: 'Connect your name.',
      body: 'A quick sign-in keeps your Court Rush progress with your Misl.',
      button: 'Continue',
      icon: UsersIcon
    },
    whatsapp: {
      eyebrow: 'Gather the Misl',
      title: 'Bring the room with you.',
      body: 'Share the invitation in your group and bring a voice back to the court.',
      button: 'Share invitation',
      icon: MessageCircleIcon
    },
    share: {
      eyebrow: 'Invitation ready',
      title: 'Your Court Rush link is live.',
      body: 'The first twelve arrivals join the opening circle.',
      button: 'View joining room',
      icon: SparklesIcon
    },
    join: {
      eyebrow: 'Joining room',
      title: 'Your Misl is forming.',
      body: 'Keep the invitation moving. The court responds when you arrive together.',
      button: 'Enter court room',
      icon: UsersIcon
    },
    contribution: {
      eyebrow: 'One small act',
      title: 'Add your voice.',
      body: 'Choose a line, a memory, or a promise to carry into the revived court.',
      button: 'Add contribution',
      icon: BookOpenIcon
    },
    challenge: {
      eyebrow: 'First court challenge',
      title: 'What makes a court live?',
      body: 'The best answer is not a perfect one. It is the one your Misl can stand behind.',
      button: 'See the tally',
      icon: SwordsIcon
    },
    leaderboard: {
      eyebrow: 'The tally',
      title: 'The Misls are moving.',
      body: 'Your Misl has advanced because its cards, invitations, and contributions are all in play.',
      button: 'Claim result',
      icon: LandmarkIcon
    },
    result: {
      eyebrow: 'Court result',
      title: 'You opened a seat.',
      body: 'Your Misl has earned a place in the first circle of the restored court.',
      button: 'Choose a seat',
      icon: CrownIcon
    },
    seats: {
      eyebrow: 'First circle',
      title: 'Take your place.',
      body: 'A seat is a promise to return—not a title to hold alone.',
      button: 'Confirm seat',
      icon: CrownIcon
    },
    attendance: {
      eyebrow: 'Attendance',
      title: 'The court remembers.',
      body: 'Your Misl is now marked present for the first gathering.',
      button: 'Complete Rush',
      icon: CheckIcon
    },
    victory: {
      eyebrow: 'Court open',
      title: 'The Court is awake.',
      body: 'The Treasury has become a living room again. Bring someone with you next time.',
      button: 'Return to court room',
      icon: SparklesIcon
    },
    admin: {
      eyebrow: 'Court steward',
      title: 'Opening ledger.',
      body: 'Track gathered Misls, released packets, and the next court threshold.',
      button: 'Back to Court Rush',
      icon: LandmarkIcon
    }
  };
  return steps[screen] ?? steps.google!;
}
function previousScreen(screen: Screen): Screen {
  const order: Screen[] = ['google', 'whatsapp', 'share', 'join', 'dashboard', 'packet', 'reveal', 'contribution', 'awakening', 'gates', 'challenge', 'leaderboard', 'result', 'seats', 'attendance', 'victory', 'admin'];
  const index = order.indexOf(screen);
  return index <= 0 ? 'misl' : order[index - 1];
}
function nextScreen(screen: Screen): Screen {
  const order: Screen[] = ['google', 'whatsapp', 'share', 'join', 'dashboard', 'packet', 'reveal', 'contribution', 'awakening', 'gates', 'challenge', 'leaderboard', 'result', 'seats', 'attendance', 'victory', 'admin'];
  const index = order.indexOf(screen);
  return index === -1 || index === order.length - 1 ? 'landing' : order[index + 1];
}